/**
 * Shared 2D path + geometry primitives for the canvas map renderer (WI #5531).
 *
 * These are the pure drawing helpers the scene painters reuse — rounded rects,
 * the activity hexagon outline, the phase chevron ribbons, the diagonal stripe
 * pattern and a point-in-rect test. They hold no scene state; every painter
 * passes its own layer context.
 *
 * Note: the path strings below contain non-ASCII bytes (extracted verbatim from
 * the SVG parts), so plain `grep` flags this file binary — use `grep -a`.
 */
import { cssVar } from './palette';

/**
 * The SVG path-data string a `Path2D` was built from, stashed on the object so the
 * vector (PDF) export ctx can replay it — a `CanvasRenderingContext2D` from the
 * `svgcanvas` shim does not accept a `Path2D` argument to `fill()`/`stroke()`, so
 * `export/svgPath.ts` re-traces the original `d` string instead. The screen
 * renderer ignores this field entirely and still draws the native `Path2D`.
 */
export const SVG_PATH_DATA = Symbol('svgPathData');

/** Build a `Path2D` and tag it with its source `d` string for vector-export replay. */
function tracedPath(d: string): Path2D {
  const path = new Path2D(d);
  (path as unknown as Record<symbol, string>)[SVG_PATH_DATA] = d;

  return path;
}

/** Exact hexagon outline from app/activity/MapHexagon.vue (natural ~30×28, centred ~0,0). */
const HEX_PATH_D =
  'm -5.457765,-13.999999 c -1.716703,-10e-4 -3.307563,0.900448 -4.188905,2.373645 -0.396164,0.661818 -5.071166,8.452801 -5.468449,9.113394 -0.929101,1.545376 -0.929894,3.477251 -0.0021,5.023389 0.411783,0.685904 5.06201,8.436971 5.472569,9.121637 0.880985,1.467674 2.466822,2.366157 4.178604,2.367462 0.859079,6.29e-4 10.097499,6.29e-4 10.955441,0 1.710217,-0.0012 3.294918,-0.897879 4.176542,-2.363341 0.43588,-0.72477 5.042097,-8.40386 5.47669,-9.129878 0.924348,-1.543202 0.924348,-3.469887 0,-5.013088 -0.420195,-0.700736 -5.055308,-8.423863 -5.47669,-9.125757 -0.882119,-1.469563 -2.470799,-2.368329 -4.184785,-2.367463 -0.8048,5.89e-4 -10.132862,5.89e-4 -10.938957,0 z';

/** The activity hexagon outline, built once and reused for every hexagon draw. */
export const hexPath = tracedPath(HEX_PATH_D);

/** Build a rounded-rect path on `ctx` (does not fill/stroke). */
export function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}

/** Chevron "phase ribbon" outline (MapPhaseMarker.vue), width-parametric. */
export function phaseMarkerPath(width: number): Path2D {
  return tracedPath(
    `m 4.6401356,1.4702298e-7 c -4.07166696,9.9699999702e-4 -6.161195,4.87737295297702 -3.353515,7.82617195297702 l 12.5451884,13.1652139 c 1.502649,1.860665 1.701706,4.910913 -0.136693,6.160676 l -12.4084954,13.021766 c -2.807681,2.948799 -0.71815296,7.825175 3.353515,7.826172 h ${width - 24}.2812504 c 1.267139,3.19e-4 2.479088,-0.518483 3.353524,-1.435547 l 18.45507,-19.369141 c 1.70475,-1.789143 1.70475,-4.601482 0,-6.390625 l -18.45507,-19.3691399 c -0.874436,-0.91706495 -2.086385,-1.43586595298 -3.353524,-1.43554695297702 z`,
  );
}

/** Smaller chevron marker used in the minimap (MapMiniPhaseMarker.vue). */
export function miniPhaseMarkerPath(width: number): Path2D {
  return tracedPath(
    `m 1.9394666,4.9295797 2.4554142,3.0692678 c 0.6051192,1.0011525 0.6051192,3.0011525 0.00144,4.0005015 l -2.3346674,2.918335 c -3.136477,4.33846 -2.22231149,4.990024 -0.050852,5.082316 h ${width - 12}.981934 l 7.003619,-7.781799 c 0.996381,-1.218201 1.00443,-1.593308 1.003643,-2.218201 -6.19e-4,-0.4916073 -0.0073,-1 -1.018127,-2.2342942 l -6.989135,-7.7657058 h ${10 - width}.9755863 c -2.13679341,0.02625709 -3.1594945,1.4434239 -0.077685,4.9295797 z`,
  );
}

/** Point-in-rect test (screen space), used for canvas hit-testing. */
export function inRect(
  x: number,
  y: number,
  rect: { x: number; y: number; w: number; h: number },
): boolean {
  return (
    x >= rect.x && x <= rect.x + rect.w && y >= rect.y && y <= rect.y + rect.h
  );
}

/**
 * Diagonal bluelight stripe pattern (selected workstream band + selected-phase
 * background). Returns null when patterns are unavailable (non-browser), so the
 * caller can fall back to a flat translucent fill.
 */
export function stripePattern(
  ctx: CanvasRenderingContext2D,
): CanvasPattern | null {
  const tile = document.createElement('canvas');
  tile.width = 7;
  tile.height = 7;
  const tctx = tile.getContext('2d');
  if (!tctx) {
    return null;
  }
  tctx.globalAlpha = 0.3;
  tctx.fillStyle = cssVar('--color-bluelight-300', '#7CD4FD');
  tctx.fillRect(0, 0, 2, 7);
  const pattern = ctx.createPattern(tile, 'repeat');
  if (
    pattern &&
    'setTransform' in pattern &&
    typeof DOMMatrix !== 'undefined'
  ) {
    pattern.setTransform(new DOMMatrix().rotate(-45));
  }

  return pattern;
}

/**
 * Diagonal gray hatch (weekend + holiday timeline shading), a 1:1 port of
 * MapTimeline's `#diagonalHatch` pattern: an 8×8 tile with three parallel 45°
 * gray-200 strokes (width 2). Returns null when patterns are unavailable.
 */
export function hatchPattern(
  ctx: CanvasRenderingContext2D,
): CanvasPattern | null {
  const tile = document.createElement('canvas');
  tile.width = 8;
  tile.height = 8;
  const tctx = tile.getContext('2d');
  if (!tctx) {
    return null;
  }
  tctx.strokeStyle = cssVar('--color-gray-200', '#E4E7EC');
  tctx.lineWidth = 2;
  tctx.beginPath();
  // Mirrors d="M-2,2 l4,-4 M0,8 l8,-8 M6,10 l4,-4".
  tctx.moveTo(-2, 2);
  tctx.lineTo(2, -2);
  tctx.moveTo(0, 8);
  tctx.lineTo(8, 0);
  tctx.moveTo(6, 10);
  tctx.lineTo(10, 6);
  tctx.stroke();

  return ctx.createPattern(tile, 'repeat');
}
